import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Box,
  Radio,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyIcon from '@mui/icons-material/VpnKey';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import api from '../../utils/functions/api';
import { showAlert, confirmAsync } from '../../utils/functions/globalDialog';

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    showAlert('คัดลอกแล้ว');
  } catch {
    showAlert('คัดลอกอัตโนมัติไม่สำเร็จ กรุณาเลือกข้อความแล้วคัดลอกด้วยตนเอง');
  }
}

const emptyProviderForm = { provider_key: '', display_name: '', key_value: '' };
const CUSTOM_PROVIDER_KEY_OPTION = '__custom__';
const emptyKeyForm = { value: '' };
const emptyModelForm = { model_id: '', label: '', capability: '', is_default: false };

// จัดการ API key/model ของ provider ภายนอก (Groq, Gemini ฯลฯ) ผ่านหน้าเว็บแทนการแก้ .env — รองรับ
// provider หลายตัว, provider นึงมีได้หลาย key (ใช้หมุนกันเวลาโดน rate limit — ดู apiProvider.service.js
// getActiveKey ฝั่ง backend) และเลือกได้ว่า provider นั้นใช้ model รุ่นไหนอยู่ (default) โดยไม่ต้องแก้โค้ด/deploy
export default function DeveloperApiProviders() {
  const apiBase = String(process.env.REACT_APP_API_URL || '').trim();

  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);

  const [providerFormOpen, setProviderFormOpen] = useState(false);
  const [providerForm, setProviderForm] = useState(emptyProviderForm);
  const [savingProvider, setSavingProvider] = useState(false);
  // ตัวเลือก dropdown "เพิ่ม Provider" — สแกนมาจาก .env ฝั่ง backend (ชื่อ <X>_API_KEY ที่ยังไม่ได้เพิ่มเป็น
  // provider ใน DB) กันพิมพ์ provider_key ผิดจนไม่ตรงกับชื่อ env var ที่ getActiveKey จะ fallback ไปหา
  const [candidateKeys, setCandidateKeys] = useState([]);
  const [useCustomProviderKey, setUseCustomProviderKey] = useState(false);

  // { providerId } | null — เปิด dialog เพิ่ม key ให้ provider นี้
  const [keyDialogProvider, setKeyDialogProvider] = useState(null);
  const [keyForm, setKeyForm] = useState(emptyKeyForm);
  const [savingKey, setSavingKey] = useState(false);

  // { providerId } | null — เปิด dialog เพิ่ม model ให้ provider นี้
  const [modelDialogProvider, setModelDialogProvider] = useState(null);
  const [modelForm, setModelForm] = useState(emptyModelForm);
  const [savingModel, setSavingModel] = useState(false);

  // { providerKey, displayName } | null — เปิด dialog ดูประวัติการใช้งานของ provider นี้
  const [usageLogProvider, setUsageLogProvider] = useState(null);
  const [usageLogRows, setUsageLogRows] = useState([]);
  const [usageLogLoading, setUsageLogLoading] = useState(false);

  const openUsageLog = async (provider) => {
    setUsageLogProvider(provider);
    setUsageLogLoading(true);
    try {
      const { data } = await api.get(`${apiBase}/api-provider/usage-log/${provider.provider_key}`);
      setUsageLogRows(Array.isArray(data) ? data : []);
    } catch (error) {
      showAlert(error?.response?.data?.error?.message || 'โหลดประวัติการใช้งานไม่สำเร็จ');
    } finally {
      setUsageLogLoading(false);
    }
  };

  // { providerKey, displayName } | null — เปิด dialog ทดสอบถาม provider นี้ (เช็คว่า key/endpoint ใช้ได้จริงไหม)
  const [askProvider, setAskProvider] = useState(null);
  const [askPrompt, setAskPrompt] = useState('สวัสดี ช่วยแนะนำตัวสั้นๆ หน่อย');
  const [askAnswer, setAskAnswer] = useState(null);
  const [askError, setAskError] = useState('');
  const [asking, setAsking] = useState(false);

  const openAskDialog = (provider) => {
    setAskProvider(provider);
    setAskAnswer(null);
    setAskError('');
  };

  const sendAsk = async () => {
    if (!askPrompt.trim()) return;
    setAsking(true);
    setAskAnswer(null);
    setAskError('');
    try {
      const { data } = await api.post(`${apiBase}/api-provider/ask/${askProvider.provider_key}`, {
        prompt: askPrompt.trim(),
      });
      setAskAnswer(data?.answer || '');
    } catch (error) {
      setAskError(error?.response?.data?.error?.message || error.message || 'เรียกไม่สำเร็จ');
    } finally {
      setAsking(false);
    }
  };

  const fetchProviders = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${apiBase}/api-provider`);
      setProviders(Array.isArray(data) ? data : []);
    } catch (error) {
      showAlert(error?.response?.data?.error?.message || 'โหลดรายการ provider ไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const openProviderForm = async () => {
    setProviderForm(emptyProviderForm);
    setUseCustomProviderKey(false);
    setProviderFormOpen(true);
    try {
      const { data } = await api.get(`${apiBase}/api-provider/candidates`);
      setCandidateKeys(Array.isArray(data) ? data : []);
    } catch (error) {
      setCandidateKeys([]);
    }
  };

  // เลือกจาก dropdown -> เติม provider_key + ชื่อที่แสดงจากแคตตาล็อกให้เลย (ยังแก้ต่อเองได้)
  // เลือก "อื่นๆ (พิมพ์เอง)" -> สลับไปโชว์ช่องพิมพ์ประเภท provider_key เองแทน สำหรับ provider ที่ไม่อยู่ในแคตตาล็อก
  const handleProviderKeySelect = (value) => {
    if (value === CUSTOM_PROVIDER_KEY_OPTION) {
      setUseCustomProviderKey(true);
      setProviderForm((f) => ({ ...f, provider_key: '' }));
      return;
    }
    const candidate = candidateKeys.find((c) => c.provider_key === value);
    setProviderForm((f) => ({
      ...f,
      provider_key: value,
      display_name: candidate?.display_name || f.display_name,
    }));
  };

  const saveProvider = async () => {
    if (!providerForm.provider_key.trim() || !providerForm.display_name.trim()) {
      showAlert('กรุณากรอก provider_key และชื่อให้ครบ');
      return;
    }
    setSavingProvider(true);
    try {
      const { data: created } = await api.post(`${apiBase}/api-provider`, {
        provider_key: providerForm.provider_key.trim().toUpperCase(),
        display_name: providerForm.display_name.trim(),
      });
      // ใส่ key มาด้วยในฟอร์มเดียวกันเลย (ไม่บังคับ — เว้นว่างไว้แล้วมาเพิ่มทีหลังจากปุ่ม "เพิ่ม Key" ในแถว
      // provider ก็ได้) ถ้าสร้าง provider สำเร็จแต่เพิ่ม key พลาด ให้แจ้งเตือนแยก ไม่ rollback provider ที่สร้างไปแล้ว
      if (providerForm.key_value.trim()) {
        try {
          await api.post(`${apiBase}/api-provider/${created.id}/keys`, {
            value: providerForm.key_value.trim(),
          });
        } catch (keyError) {
          showAlert(
            `สร้าง provider สำเร็จ แต่เพิ่ม key ไม่สำเร็จ: ${keyError?.response?.data?.error?.message || keyError.message} — กดปุ่ม "เพิ่ม Key" ในแถว provider เพื่อลองใหม่`,
          );
        }
      }
      setProviderFormOpen(false);
      fetchProviders();
    } catch (error) {
      showAlert(error?.response?.data?.error?.message || 'บันทึก provider ไม่สำเร็จ');
    } finally {
      setSavingProvider(false);
    }
  };

  const deleteProvider = async (provider) => {
    if (!(await confirmAsync(`ลบ provider "${provider.display_name}" ทั้งหมด (รวม key/model ในนี้) ใช่ไหม?`))) return;
    try {
      await api.delete(`${apiBase}/api-provider/${provider.id}`);
      fetchProviders();
    } catch (error) {
      showAlert(error?.response?.data?.error?.message || 'ลบ provider ไม่สำเร็จ');
    }
  };

  const openKeyDialog = (providerId) => {
    setKeyForm(emptyKeyForm);
    setKeyDialogProvider(providerId);
  };

  const saveKey = async () => {
    if (!keyForm.value.trim()) {
      showAlert('กรุณากรอกค่า key');
      return;
    }
    setSavingKey(true);
    try {
      await api.post(`${apiBase}/api-provider/${keyDialogProvider}/keys`, {
        value: keyForm.value.trim(),
      });
      setKeyDialogProvider(null);
      fetchProviders();
    } catch (error) {
      showAlert(error?.response?.data?.error?.message || 'เพิ่ม key ไม่สำเร็จ');
    } finally {
      setSavingKey(false);
    }
  };

  const toggleKeyActive = async (providerId, key) => {
    try {
      await api.put(`${apiBase}/api-provider/${providerId}/keys/${key.id}`, { active: !key.active });
      fetchProviders();
    } catch (error) {
      showAlert(error?.response?.data?.error?.message || 'อัปเดต key ไม่สำเร็จ');
    }
  };

  const deleteKey = async (providerId, key) => {
    if (!(await confirmAsync(`ลบ key "${key.label || key.masked_preview}" ใช่ไหม?`))) return;
    try {
      await api.delete(`${apiBase}/api-provider/${providerId}/keys/${key.id}`);
      fetchProviders();
    } catch (error) {
      showAlert(error?.response?.data?.error?.message || 'ลบ key ไม่สำเร็จ');
    }
  };

  // คัดลอกค่าเต็ม (ไม่ใช่ masked_preview) — เรียกถอดรหัสจาก backend สดๆ ตอนกดเท่านั้น (ไม่ cache/setState
  // ค้างไว้ในหน้าเว็บเลยนอกจากช่วงสั้นๆ ระหว่างรอ fetch) แล้วคัดลอกเข้า clipboard ทันทีโดยไม่ render ค่าเต็ม
  // ลงหน้าจอเลยสักจุด (ลดความเสี่ยงถูกมองข้ามไหล่/แคปหน้าจอ)
  const [copyingKeyId, setCopyingKeyId] = useState(null);
  const copyFullKey = async (providerId, key) => {
    setCopyingKeyId(key.id);
    try {
      const { data } = await api.get(`${apiBase}/api-provider/${providerId}/keys/${key.id}/reveal`);
      await copyText(data.value);
    } catch (error) {
      showAlert(error?.response?.data?.error?.message || 'คัดลอกค่าเต็มไม่สำเร็จ');
    } finally {
      setCopyingKeyId(null);
    }
  };

  const openModelDialog = (providerId) => {
    setModelForm(emptyModelForm);
    setModelDialogProvider(providerId);
  };

  const saveModel = async () => {
    if (!modelForm.model_id.trim()) {
      showAlert('กรุณากรอก model id');
      return;
    }
    setSavingModel(true);
    try {
      await api.post(`${apiBase}/api-provider/${modelDialogProvider}/models`, {
        model_id: modelForm.model_id.trim(),
        label: modelForm.label.trim(),
        capability: modelForm.capability.trim(),
        is_default: modelForm.is_default,
      });
      setModelDialogProvider(null);
      fetchProviders();
    } catch (error) {
      showAlert(error?.response?.data?.error?.message || 'เพิ่ม model ไม่สำเร็จ');
    } finally {
      setSavingModel(false);
    }
  };

  const toggleModelActive = async (providerId, model) => {
    try {
      await api.put(`${apiBase}/api-provider/${providerId}/models/${model.id}`, { active: !model.active });
      fetchProviders();
    } catch (error) {
      showAlert(error?.response?.data?.error?.message || 'อัปเดต model ไม่สำเร็จ');
    }
  };

  const setModelDefault = async (providerId, model) => {
    try {
      await api.put(`${apiBase}/api-provider/${providerId}/models/${model.id}`, { is_default: true });
      fetchProviders();
    } catch (error) {
      showAlert(error?.response?.data?.error?.message || 'ตั้ง default model ไม่สำเร็จ');
    }
  };

  const deleteModel = async (providerId, model) => {
    if (!(await confirmAsync(`ลบ model "${model.model_id}" ใช่ไหม?`))) return;
    try {
      await api.delete(`${apiBase}/api-provider/${providerId}/models/${model.id}`);
      fetchProviders();
    } catch (error) {
      showAlert(error?.response?.data?.error?.message || 'ลบ model ไม่สำเร็จ');
    }
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardHeader
        title="AI Provider Keys"
        subheader="จัดการ API key และ model ของผู้ให้บริการ AI เท่านั้น (Groq, Gemini ฯลฯ) แทนการแก้ .env — API key อื่นที่ไม่ใช่ AI (FCM, PostHog ฯลฯ) ยังอยู่ใน .env ตามเดิม ไม่เกี่ยวกับหน้านี้ — โค้ดที่เรียกใช้ provider เหล่านี้จะหมุน key ในพูลอัตโนมัติเวลาโดน rate limit"
        action={
          <Button startIcon={<AddIcon />} onClick={openProviderForm} variant="contained" size="small">
            เพิ่ม Provider
          </Button>
        }
      />
      <CardContent>
        {loading && <Typography color="text.secondary">กำลังโหลด...</Typography>}
        {!loading && providers.length === 0 && (
          <Typography color="text.secondary">ยังไม่มี provider — กด "เพิ่ม Provider" เพื่อเริ่มต้น</Typography>
        )}
        <Stack spacing={2}>
          {providers.map((provider) => (
            <Paper key={provider.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <KeyIcon fontSize="small" color="action" />
                  <Typography variant="subtitle1" fontWeight={700}>{provider.display_name}</Typography>
                  <Chip label={provider.provider_key} size="small" />
                </Stack>
                <Stack direction="row" spacing={0.5}>
                  <Button size="small" variant="outlined" onClick={() => openAskDialog(provider)}>
                    ทดสอบถาม
                  </Button>
                  <Tooltip title="ประวัติการใช้งาน">
                    <IconButton size="small" onClick={() => openUsageLog(provider)}>
                      <HistoryIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="ลบ provider นี้ทั้งหมด">
                    <IconButton size="small" color="error" onClick={() => deleteProvider(provider)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>

              <Box sx={{ mb: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" fontWeight={600}>Keys</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={() => openKeyDialog(provider.id)}>
                    เพิ่ม Key
                  </Button>
                </Stack>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ค่า (masked)</TableCell>
                        <TableCell align="right">ใช้ไปแล้ว</TableCell>
                        <TableCell>ใช้ล่าสุด</TableCell>
                        <TableCell>โควต้าเหลือ</TableCell>
                        <TableCell align="center">เปิดใช้</TableCell>
                        <TableCell align="center">ลบ</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {provider.keys.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <Typography color="text.secondary" variant="body2">
                              ยังไม่มี key ใน DB — ระบบจะ fallback ไปใช้ {provider.provider_key}_API_KEY จาก .env แทน
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {provider.keys.map((key) => (
                        <TableRow key={key.id}>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <code>{key.masked_preview}</code>
                              <Tooltip title="คัดลอกค่าเต็ม">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={copyingKeyId === key.id}
                                    onClick={() => copyFullKey(provider.id, key)}
                                  >
                                    {copyingKeyId === key.id
                                      ? <CircularProgress size={14} />
                                      : <ContentCopyIcon sx={{ fontSize: 14 }} />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                          <TableCell align="right">{key.use_count}</TableCell>
                          <TableCell>
                            {key.last_used_at ? new Date(key.last_used_at).toLocaleString('th-TH') : '-'}
                          </TableCell>
                          <TableCell>
                            {key.remaining_requests !== null && key.remaining_requests !== undefined ? (
                              <Typography variant="caption" component="div">
                                {key.remaining_requests}/{key.limit_requests} req · {key.remaining_tokens}/{key.limit_tokens} token
                              </Typography>
                            ) : '-'}
                          </TableCell>
                          <TableCell align="center">
                            <Switch
                              size="small"
                              checked={!!key.active}
                              onChange={() => toggleKeyActive(provider.id, key)}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton size="small" color="error" onClick={() => deleteKey(provider.id, key)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" fontWeight={600}>Models</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={() => openModelDialog(provider.id)}>
                    เพิ่ม Model
                  </Button>
                </Stack>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell align="center">Default</TableCell>
                        <TableCell>Model ID</TableCell>
                        <TableCell>ป้ายชื่อ</TableCell>
                        <TableCell>Capability</TableCell>
                        <TableCell align="center">เปิดใช้</TableCell>
                        <TableCell align="center">ลบ</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {provider.models.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <Typography color="text.secondary" variant="body2">
                              ยังไม่มี model ใน DB — โค้ดที่เรียก provider นี้จะใช้ค่า fallback ที่ฝังในโค้ดแทน
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {provider.models.map((model) => (
                        <TableRow key={model.id}>
                          <TableCell align="center">
                            <Tooltip title="ตั้งเป็น default">
                              <Radio
                                size="small"
                                checked={!!model.is_default}
                                onChange={() => setModelDefault(provider.id, model)}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell><code>{model.model_id}</code></TableCell>
                          <TableCell>{model.label || '-'}</TableCell>
                          <TableCell>{model.capability || '-'}</TableCell>
                          <TableCell align="center">
                            <Switch
                              size="small"
                              checked={!!model.active}
                              onChange={() => toggleModelActive(provider.id, model)}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton size="small" color="error" onClick={() => deleteModel(provider.id, model)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          ))}
        </Stack>
      </CardContent>

      {/* เพิ่ม provider ใหม่ */}
      <Dialog open={providerFormOpen} onClose={() => setProviderFormOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>เพิ่ม Provider</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!useCustomProviderKey ? (
              <FormControl fullWidth>
                <InputLabel id="provider-key-select-label">Provider</InputLabel>
                <Select
                  labelId="provider-key-select-label"
                  label="Provider"
                  value={providerForm.provider_key}
                  onChange={(e) => handleProviderKeySelect(e.target.value)}
                  displayEmpty
                >
                  {candidateKeys.length === 0 && (
                    <MenuItem value="" disabled>เพิ่มครบทุก provider ในแคตตาล็อกแล้ว</MenuItem>
                  )}
                  {candidateKeys.map((c) => (
                    <MenuItem key={c.provider_key} value={c.provider_key}>{c.display_name}</MenuItem>
                  ))}
                  <MenuItem value={CUSTOM_PROVIDER_KEY_OPTION}>อื่นๆ (พิมพ์เอง)</MenuItem>
                </Select>
              </FormControl>
            ) : (
              <TextField
                label="Provider (เช่น GROQ, GEMINI)"
                value={providerForm.provider_key}
                onChange={(e) => setProviderForm((f) => ({ ...f, provider_key: e.target.value }))}
                helperText={
                  <>
                    ตัวพิมพ์ใหญ่ — โค้ดฝั่ง backend ใช้ค่านี้อ้างอิง เปลี่ยนทีหลังไม่ได้{' '}
                    <Button size="small" onClick={() => setUseCustomProviderKey(false)} sx={{ p: 0, minWidth: 0 }}>
                      เลือกจากรายการแทน
                    </Button>
                  </>
                }
                fullWidth
              />
            )}
            <TextField
              label="ชื่อที่แสดง"
              value={providerForm.display_name}
              onChange={(e) => setProviderForm((f) => ({ ...f, display_name: e.target.value }))}
              fullWidth
            />
            <Typography variant="caption" color="text.secondary">
              ใส่ API Key เลยก็ได้ (ไม่บังคับ — เว้นว่างไว้แล้วมาเพิ่มทีหลังจากปุ่ม "เพิ่ม Key" ในแถว provider ก็ได้)
            </Typography>
            <TextField
              label="ค่า API Key"
              value={providerForm.key_value}
              onChange={(e) => setProviderForm((f) => ({ ...f, key_value: e.target.value }))}
              helperText="ค่าจะถูกเข้ารหัสก่อนเก็บลงฐานข้อมูล — หลังบันทึกจะเห็นแค่ค่า masked เท่านั้น"
              multiline
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProviderFormOpen(false)}>ยกเลิก</Button>
          <Button variant="contained" onClick={saveProvider} disabled={savingProvider}>บันทึก</Button>
        </DialogActions>
      </Dialog>

      {/* เพิ่ม key */}
      <Dialog open={!!keyDialogProvider} onClose={() => setKeyDialogProvider(null)} maxWidth="sm" fullWidth>
        <DialogTitle>เพิ่ม API Key</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="ค่า Key"
              value={keyForm.value}
              onChange={(e) => setKeyForm((f) => ({ ...f, value: e.target.value }))}
              helperText="ค่าจะถูกเข้ารหัสก่อนเก็บลงฐานข้อมูล — หลังบันทึกจะเห็นแค่ค่า masked เท่านั้น"
              multiline
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKeyDialogProvider(null)}>ยกเลิก</Button>
          <Button variant="contained" onClick={saveKey} disabled={savingKey}>บันทึก</Button>
        </DialogActions>
      </Dialog>

      {/* เพิ่ม model */}
      <Dialog open={!!modelDialogProvider} onClose={() => setModelDialogProvider(null)} maxWidth="sm" fullWidth>
        <DialogTitle>เพิ่ม Model</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Model ID (เช่น qwen/qwen3.8-27b)"
              value={modelForm.model_id}
              onChange={(e) => setModelForm((f) => ({ ...f, model_id: e.target.value }))}
              fullWidth
            />
            <TextField
              label="ป้ายชื่อ (ไม่บังคับ)"
              value={modelForm.label}
              onChange={(e) => setModelForm((f) => ({ ...f, label: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Capability (ไม่บังคับ เช่น vision, text)"
              value={modelForm.capability}
              onChange={(e) => setModelForm((f) => ({ ...f, capability: e.target.value }))}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModelDialogProvider(null)}>ยกเลิก</Button>
          <Button variant="contained" onClick={saveModel} disabled={savingModel}>บันทึก</Button>
        </DialogActions>
      </Dialog>

      {/* ทดสอบถาม */}
      <Dialog open={!!askProvider} onClose={() => setAskProvider(null)} maxWidth="sm" fullWidth>
        <DialogTitle>ทดสอบถาม — {askProvider?.display_name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="คำถาม"
              value={askPrompt}
              onChange={(e) => setAskPrompt(e.target.value)}
              multiline
              minRows={2}
              fullWidth
            />
            {asking && <Typography color="text.secondary">กำลังถาม...</Typography>}
            {askAnswer !== null && !asking && (
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'success.light' }}>
                <Typography variant="body2" whiteSpace="pre-wrap">{askAnswer}</Typography>
              </Paper>
            )}
            {askError && !asking && (
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'error.light' }}>
                <Typography variant="body2">{askError}</Typography>
              </Paper>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAskProvider(null)}>ปิด</Button>
          <Button variant="contained" onClick={sendAsk} disabled={asking}>ถาม</Button>
        </DialogActions>
      </Dialog>

      {/* ประวัติการใช้งาน */}
      <Dialog open={!!usageLogProvider} onClose={() => setUsageLogProvider(null)} maxWidth="sm" fullWidth>
        <DialogTitle>ประวัติการใช้งาน — {usageLogProvider?.display_name}</DialogTitle>
        <DialogContent>
          {usageLogLoading && <Typography color="text.secondary">กำลังโหลด...</Typography>}
          {!usageLogLoading && usageLogRows.length === 0 && (
            <Typography color="text.secondary">ยังไม่มีประวัติการใช้งาน</Typography>
          )}
          {!usageLogLoading && usageLogRows.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">ผลลัพธ์</TableCell>
                    <TableCell>ฟีเจอร์</TableCell>
                    <TableCell>ผู้ใช้</TableCell>
                    <TableCell>เวลา</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usageLogRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell align="center">
                        {row.success
                          ? <CheckCircleIcon fontSize="small" color="success" />
                          : <CancelIcon fontSize="small" color="error" />}
                      </TableCell>
                      <TableCell>{row.module || '-'}</TableCell>
                      <TableCell>{row.user_name || 'ไม่ทราบ (ไม่ได้ login)'}</TableCell>
                      <TableCell>{new Date(row.createdAt).toLocaleString('th-TH')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsageLogProvider(null)}>ปิด</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
